<template>
  <section>
    <button
      :disabled="isEdition"
      class="w-100 form-input addTask-button"
      type="button"
      @click="handleOpenModal"
      required
    >
      Add New Task <b>+</b>
    </button>
    <modal
      :title="`${isTaskEdition ? 'Edit' : 'Register'} Task`"
      :maxWidth="'465px'"
      :showModal="isTogglingModal"
      @closeModal="handleCloseModal"
    >
      <template v-slot:content>
        <form class="form" @submit.prevent="handleSubmitRegister">
          <div class="form-group m-2">
            <label for="taskType" class="flex mb-1 text-muted">Select Task Type</label>
            <select
              @change="handleTaskTypeChange"
              class="w-100 form-select"
              id="taskType"
              v-model="taskData.taskType"
              required
            >
              <option value="" selected disabled hidden>Choose a task type</option>
              <option :value="TASKS_TYPES.SURVEY">Survey</option>
              <option :value="TASKS_TYPES.EXERCISE">Exercise</option>
            </select>
          </div>
          <div class="form-group m-2" v-if="taskData.taskType === TASKS_TYPES.SURVEY">
            <label for="taskValue" class="flex mb-1 text-muted">Select Survey</label>
            <select
              class="w-100 form-select"
              id="taskValue"
              v-model="taskData.taskValue"
              :key="taskData.taskType"
              required
            >
              <option value="" selected disabled hidden>Choose one survey</option>
              <option v-for="survey in surveys" :value="survey.id">
                {{ survey.name }}
              </option>
            </select>
          </div>
          <div class="form-group m-2" v-else-if="taskData.taskType === TASKS_TYPES.EXERCISE">
            <label for="taskValue" class="flex mb-1 text-muted">Select Exercise</label>
            <select
              class="w-100 form-select"
              id="taskValue"
              v-model="taskData.taskValue"
              :key="taskData.taskType"
              required
            >
              <option value="" selected disabled hidden>Choose one exercise</option>
              <option v-for="exercise in exercises" :value="exercise.id">
                {{ exercise.name }}
              </option>
            </select>
          </div>
          <section class="form-group m-2 task-repetitions">
            <div>
              <div>
                <label for="taskRepetitions" class="flex mb-1 text-muted">Task Frequency</label>
                <div class="mt-2 mb-2 form-switch-task">
                  <p>Frequency</p>
                  <SwitchInput v-model="taskData.taskIsSpecificDays" class="mt-0" />
                  <section class="dynamic-repetitions">
                    <p>Specific Days</p>
                    <Tooltip>
                      <template #tooltip-trigger>
                        <img src="@/assets/icons/info.svg" alt="Info Icon" />
                      </template>
                      <template #tooltip-content>
                        <p>
                          <strong>Specific Days:</strong> Set the task to occur on custom days
                          (e.g., day 1, 8, 26).<br />
                          <strong>Frequency:</strong> Automatically repeats the task every
                          set number of days (e.g., every 2 days) after completion.<br /><br />
                          Use the input below to define your preferred interval or day numbers.
                        </p>
                      </template>
                    </Tooltip>
                  </section>
                </div>
              </div>

              <template v-if="taskData.taskIsSpecificDays">
                <input
                  class="w-100 form-input"
                  type="text"
                  id="taskSpecificDays"
                  v-model="taskData.taskSpecificDays"
                  required
                />
              </template>
              <template v-else>
                <input
                  class="w-100 form-input"
                  type="number"
                  id="taskFrequency"
                  v-model="taskData.taskFrequency"
                  required
                />
              </template>
              <small class="text-muted text-specific-days">
                {{ messageHelp }}
              </small>
            </div>
          </section>
          <div v-if="!taskData.taskIsSpecificDays" class="form-group m-2">
            <label for="taskDelay" class="flex mb-1 text-muted">Task Delay (in Days)</label>
            <input
              class="w-100 form-input"
              type="number"
              id="taskDelay"
              min="0"
              max="100"
              v-model="taskData.taskDelay"
              required
            />
          </div>
          <div v-if="!taskData.taskIsSpecificDays" class="form-group m-2">
            <label for="taskFrequency" class="flex mb-1 text-muted">Task Repetitions</label>
            <input
              class="w-100 form-input"
              type="number"
              id="taskFrequency"
              v-model="taskData.taskRepetitions"
              required
            />
          </div>
          <div class="form-group m-2">
            <label for="taskName" class="flex mb-1 text-muted">Task Name</label>
            <input
              class="w-100 form-input"
              type="text"
              placeholder="Task Name"
              id="taskName"
              v-model="taskData.taskName"
              required
            />
          </div>
          <div class="form-group m-2">
            <label for="taskSeriesNumber" class="flex mb-1 text-muted"
              >Series number (The repetition count for each exercise)</label
            >
            <input
              class="w-100 form-input"
              type="number"
              placeholder="Series number"
              id="taskSeriesNumber"
              min="1"
              v-model="taskData.taskSeriesNumber"
              required
            />
          </div>
          <div class="form-group m-2">
            <label for="taskOpenWindow" class="flex mb-1 text-muted">Task Open Window (in Days)</label>
            <input
              class="w-100 form-input"
              type="number"
              placeholder="0"
              id="taskOpenWindow"
              min="1"
              v-model="taskData.taskOpenWindow"
              required
            />
          </div>
          <div class="flex gap-3 mt-5 w-100">
            <button
              class="m-auto btn btn-primary disabled-button-task"
              type="submit"
              :disabled="!isValidTaskSpecificDays"
            >
              {{ isTaskEdition ? 'Save Changes' : 'Register Task' }}
            </button>
          </div>
        </form>
      </template>
    </modal>
  </section>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import Modal from '@/components/Modal';
import { toast } from 'vue3-toastify';
import { TASKS_TYPES } from '@/common/constants';
import { getSurveys, getExercises } from '@/studies/services/tasks';
import SwitchInput from '@/components/SwitchInput.vue';
import Tooltip from '@/components/Tooltip.vue';

const DIALOG_MESSAGES = {
  EDIT: 'The task has been edited successfully',
  ADD: 'The task has been added successfully',
};

const emit = defineEmits(['addTask', 'editTask', 'exitTaskEdition']);

const props = defineProps({
  isEdition: {
    type: Boolean,
    required: false,
    default: false,
  },
  isTaskEdition: {
    type: Boolean,
    required: false,
    default: false,
  },
  selectedTask: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const defaultTaskInformation = {
  taskType: '',
  taskValue: '',
  taskDelay: 0,
  taskRepetitions: 1,
  taskFrequency: 0,
  taskName: '',
  taskSeriesNumber: 0,
  taskOpenWindow: 0,
  taskSpecificDays: '',
  taskIsSpecificDays: false,
};

const isValidTaskSpecificDays = ref(false);

const isTogglingModal = ref(false);
const taskData = ref({ ...defaultTaskInformation });
const surveys = ref([]);
const exercises = ref([]);

function handleOpenModal() {
  isTogglingModal.value = true;
}

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const messageHelp = computed(() => {
  if (taskData.value.taskIsSpecificDays) {
    return 'Specify the exact days on which the tasks should run, separated by commas (e.g., 1, 2, 5, 24). Ensure that the days are listed in coherent, ascending order, from lowest to highest, and that commas are properly used as separators.';
  }
  return 'Example: If you want the task to repeat every day, enter 1. If you want it to repeat every 2 days, just enter 2.';
});

const validateTaskSpecificDays = () => {
  if (!taskData.value.taskIsSpecificDays) {
    const isValid =
      Number.isInteger(Number(taskData.value.taskFrequency)) &&
      Number(taskData.value.taskFrequency) > 0;
    return isValid;
  }

  const input = taskData.value.taskSpecificDays?.trim();

  const regex = /^\d+(?:\s*,\s*\d+)*$/;
  if (!regex.test(input)) return false;
  const values = input.split(',').map((value) => Number(value.trim()));
  for (let i = 1; i < values.length; i++) {
    if (values[i] <= values[i - 1]) return false;
  }
  return true;
};

watch(
  () => [
    taskData.value.taskSpecificDays,
    taskData.value.taskFrequency,
    taskData.value.taskIsSpecificDays,
  ],
  debounce(() => {
    isValidTaskSpecificDays.value = validateTaskSpecificDays();
  }, 300)
);

function handleCloseModal() {
  isTogglingModal.value = false;
  taskData.value = { ...defaultTaskInformation };
  if (props.isTaskEdition) emit('exitTaskEdition');
}

function handleTaskTypeChange() {
  taskData.value.taskValue = '';
}

async function handleSubmitRegister() {
  try {
    if (props.isTaskEdition) {
      emit('editTask', { ...taskData.value, edited: true });
      toast.success(DIALOG_MESSAGES.EDIT);
      isTogglingModal.value = false;
    } else {
      emit('addTask', taskData.value);
      toast.success(DIALOG_MESSAGES.ADD);
      handleCloseModal();
    }
  } catch (error) {
    toast.error(error.message);
  }
}

onMounted(async () => {
  try {
    surveys.value = await getSurveys();
    exercises.value = await getExercises();
  } catch (error) {
    toast.error(error.message);
  }
});

watch(
  () => props.selectedTask,
  () => {
    taskData.value = {
      ...props.selectedTask,
      taskIsSpecificDays: props.selectedTask.taskIsSpecificDays,
    };
    isTogglingModal.value = true;
  }
);
</script>

<style lang="scss" scoped>
.form-switch-task {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  font-size: 10pt;
  width: fit-content;
}

.text-specific-days {
  font-size: 8pt;
  text-align: left !important;
}

.dynamic-repetitions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.task-repetitions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.wrapper-form {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  height: 100vh;
  background: $background-signup;

  .card-header {
    margin-bottom: 2rem;

    h1#title {
      font-size: 2rem;
    }

    span {
      font-size: 10pt;
    }

    @media screen and (max-width: 510px) {
      margin-bottom: 1rem;
    }
  }

  .form {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .form-group {
      margin-bottom: 1rem;
      display: flex;
      flex-direction: column;

      label {
        text-align: start;
        margin-bottom: 3px;
        font-size: 10pt;
      }

      a {
        width: 100%;
        display: flex;
        justify-content: flex-end;
      }
    }

    button#b-form {
      margin-top: 1rem;
    }
  }
}

.disabled-button-task {
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.addTask-button {
  &:not(:disabled):hover {
    background-color: lightgrey;
    border: 1px solid black;
  }
  &:disabled {
    cursor: default;
  }
}
</style>
